import React, { useState } from 'react';
import { Link } from 'react-router-dom';

function Results({ score, timeTaken, questions, userAnswers, onReturnToHome }) {
    const totalQuestions = questions?.length || 0;
    const percentage = totalQuestions > 0 ? Math.round((score / totalQuestions) * 100) : 0;
    const [filter, setFilter] = useState('all'); // 'all', 'correct', 'incorrect'

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        if (mins === 0) return `${secs} seconds`;
        return `${mins}m ${secs}s`;
    };

    const getScoreMessage = () => {
        if (percentage >= 90) return { emoji: '🎉', text: 'Outstanding!', color: 'text-green-600' };
        if (percentage >= 80) return { emoji: '🌟', text: 'Great Job!', color: 'text-green-600' };
        if (percentage >= 70) return { emoji: '👍', text: 'Good Work!', color: 'text-yellow-600' };
        if (percentage >= 60) return { emoji: '💪', text: 'Keep Practicing!', color: 'text-yellow-600' };
        return { emoji: '📚', text: 'More Study Needed', color: 'text-red-600' };
    };

    const scoreMessage = getScoreMessage();

    const filteredQuestions = questions?.filter((q) => {
        const userAnswer = userAnswers[q._id];
        const isCorrect = userAnswer === q.correctAnswer;
        if (filter === 'correct') return isCorrect;
        if (filter === 'incorrect') return !isCorrect;
        return true;
    }) || [];

    const correctCount = score;
    const incorrectCount = totalQuestions - score;

    return (
        <div className="min-h-screen bg-gray-50 font-quicksand">
            {/* Header */}
            <div className={`py-12 px-6 ${percentage >= 70 ? 'bg-gradient-to-r from-study-teal to-study-pink' : 'bg-gradient-to-r from-study-pink to-study-yellow'}`}>
                <div className="max-w-4xl mx-auto text-center">
                    <div className="text-6xl mb-4">{scoreMessage.emoji}</div>
                    <h1 className={`text-3xl md:text-4xl font-bold mb-2 ${scoreMessage.color}`}>
                        {scoreMessage.text}
                    </h1>
                    <p className="text-study-black text-lg">
                        You've completed the quiz!
                    </p>
                </div>
            </div>

            {/* Score Card */}
            <div className="max-w-4xl mx-auto px-4 -mt-8">
                <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Score Circle */}
                        <div className="text-center md:border-r border-gray-200">
                            <div className="relative inline-block">
                                <svg className="w-32 h-32" viewBox="0 0 100 100">
                                    <circle
                                        className="text-gray-200"
                                        strokeWidth="8"
                                        stroke="currentColor"
                                        fill="transparent"
                                        r="40"
                                        cx="50"
                                        cy="50"
                                    />
                                    <circle
                                        className={percentage >= 70 ? 'text-green-500' : percentage >= 50 ? 'text-yellow-500' : 'text-red-500'}
                                        strokeWidth="8"
                                        strokeDasharray={`${percentage * 2.51} 251`}
                                        strokeLinecap="round"
                                        stroke="currentColor"
                                        fill="transparent"
                                        r="40"
                                        cx="50"
                                        cy="50"
                                        transform="rotate(-90 50 50)"
                                    />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-3xl font-bold text-study-black">{percentage}%</span>
                                </div>
                            </div>
                            <p className="mt-2 text-gray-600">Score</p>
                        </div>

                        {/* Stats */}
                        <div className="flex flex-col justify-center space-y-4">
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">Correct</span>
                                <span className="text-xl font-bold text-green-600">{correctCount}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">Incorrect</span>
                                <span className="text-xl font-bold text-red-500">{incorrectCount}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">Total</span>
                                <span className="text-xl font-bold text-study-black">{totalQuestions}</span>
                            </div>
                        </div>

                        {/* Time & Actions */}
                        <div className="text-center md:border-l border-gray-200 flex flex-col justify-center">
                            <div className="text-4xl mb-2">⏱️</div>
                            <p className="text-2xl font-bold text-study-black">{formatTime(timeTaken)}</p>
                            <p className="text-gray-600 text-sm">Time Taken</p>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-8 pt-6 border-t border-gray-200 flex flex-wrap gap-4 justify-center">
                        <button
                            onClick={onReturnToHome}
                            className="bg-gradient-to-r from-study-teal to-study-pink text-study-black font-bold py-3 px-8 rounded-xl hover:shadow-lg transition-all"
                        >
                            Take Another Quiz
                        </button>
                        <Link
                            to="/progress"
                            className="bg-gray-100 text-study-black font-bold py-3 px-8 rounded-xl hover:bg-gray-200 transition-all"
                        >
                            View Progress
                        </Link>
                    </div>
                </div>
            </div>

            {/* Answer Review */}
            <div className="max-w-4xl mx-auto px-4 py-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-study-black mb-4 md:mb-0">
                        Review Your Answers
                    </h2>
                    {/* Filter Buttons */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => setFilter('all')}
                            className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                                filter === 'all'
                                    ? 'bg-study-black text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            All ({totalQuestions})
                        </button>
                        <button
                            onClick={() => setFilter('correct')}
                            className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                                filter === 'correct'
                                    ? 'bg-green-600 text-white'
                                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                        >
                            Correct ({correctCount})
                        </button>
                        <button
                            onClick={() => setFilter('incorrect')}
                            className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                                filter === 'incorrect'
                                    ? 'bg-red-600 text-white'
                                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                            }`}
                        >
                            Incorrect ({incorrectCount})
                        </button>
                    </div>
                </div>

                {/* Questions List */}
                <div className="space-y-4">
                    {filteredQuestions.map((q, index) => {
                        const userAnswer = userAnswers[q._id] || null;
                        const isCorrect = userAnswer === q.correctAnswer;
                        const originalIndex = questions.findIndex(oq => oq._id === q._id);

                        return (
                            <div
                                key={q._id || index}
                                className={`bg-white rounded-2xl shadow-md border-l-4 ${
                                    isCorrect ? 'border-green-500' : 'border-red-500'
                                } overflow-hidden`}
                            >
                                {/* Question Header */}
                                <div className={`px-6 py-4 ${isCorrect ? 'bg-green-50' : 'bg-red-50'}`}>
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-3">
                                            <span className={`text-2xl`}>
                                                {isCorrect ? '✅' : '❌'}
                                            </span>
                                            <span className="font-bold text-study-black">
                                                Question {originalIndex + 1}
                                            </span>
                                            {q.subject && (
                                                <span className="bg-study-teal/20 text-study-black text-xs font-semibold px-2 py-1 rounded-full">
                                                    {q.subject}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Question Content */}
                                <div className="px-6 py-4">
                                    <p className="text-study-black font-medium mb-4">
                                        {q.text}
                                    </p>

                                    {/* Answers */}
                                    <div className="space-y-2">
                                        {q.options?.map((option, optIndex) => {
                                            const isUserAnswer = option === userAnswer;
                                            const isCorrectAnswer = option === q.correctAnswer;
                                            let optionClass = 'bg-gray-50 border-gray-200';

                                            if (isCorrectAnswer) {
                                                optionClass = 'bg-green-50 border-green-300';
                                            } else if (isUserAnswer && !isCorrect) {
                                                optionClass = 'bg-red-50 border-red-300';
                                            }

                                            return (
                                                <div
                                                    key={optIndex}
                                                    className={`p-3 rounded-lg border ${optionClass}`}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <span className={isCorrectAnswer ? 'text-green-700 font-medium' : isUserAnswer ? 'text-red-700' : 'text-gray-600'}>
                                                            {option}
                                                        </span>
                                                        <div className="flex gap-2">
                                                            {isUserAnswer && (
                                                                <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                                                                    Your answer
                                                                </span>
                                                            )}
                                                            {isCorrectAnswer && (
                                                                <span className="text-xs bg-green-200 text-green-700 px-2 py-1 rounded">
                                                                    Correct
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {!userAnswer && (
                                            <p className="text-sm text-gray-500 italic">
                                                You did not answer this question
                                            </p>
                                        )}
                                    </div>

                                    {/* Explanation */}
                                    {q.explanation && (
                                        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                            <p className="text-sm text-blue-800">
                                                <span className="font-bold">Explanation:</span> {q.explanation}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {filteredQuestions.length === 0 && (
                    <div className="text-center py-12">
                        <div className="text-4xl mb-2">📭</div>
                        <p className="text-gray-600">No questions match this filter</p>
                    </div>
                )}

                {/* Bottom Action */}
                <div className="mt-8 text-center">
                    <button
                        onClick={onReturnToHome}
                        className="bg-gradient-to-r from-study-pink to-study-teal text-study-black font-bold py-4 px-12 rounded-xl hover:shadow-lg transition-all text-lg"
                    >
                        Build Another Quiz
                    </button>
                </div>
            </div>
        </div>
    );
}

export default Results;